import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    nav: {
      home: 'Home',
      posDevices: 'POS Devices',
      wallet: 'Wallet',
      whoIsRoyaFor: 'Who is Roya for?',
      about: 'About Us',
      contact: 'Contact'
    },
    home: {
      heroTitle: 'Fintech Smart Payments for Everyone',
      heroSubtitle: "Let's Grow Together",
      heroDescription: 'ROYA ecosystem helps merchants and individuals grow hand in hand',
      getStarted: 'Get Started',
      learnMore: 'Learn More',
      featuresTitle: 'Our Services',
      visionTitle: 'Our Vision',
      visionDescription: 'Developed in Saudi Arabia, for Saudi Arabia — engineered to accelerate digital payments in line with Vision 2030. ROYA is not just a fintech application — it is a complete Saudi financial ecosystem designed to integrate payments, wallets, POS, QR, business tools, and merchant services under one unified platform.',
      services: {
        qrPayments: 'QR Payments',
        qrDesc: 'Quick and secure QR code payments',
        posDevices: 'POS Devices',
        posDesc: 'Modern wireless payment terminals',
        digitalWallet: 'Digital Wallet',
        walletDesc: 'Personal and merchant wallet solutions',
        instantTransfers: 'Instant Transfers',
        transferDesc: 'Send and receive money instantly',
        paymentGateway: 'Payment Gateway',
        gatewayDesc: 'Complete payment solutions for businesses',
        virtualCards: 'Virtual Cards',
        virtualDesc: 'Digital cards for online payments',
        physicalCards: 'Physical Cards',
        physicalDesc: 'Premium payment cards',
        loans: 'Loans',
        loansDesc: 'Coming Soon'
      },
      features: {
        scan: 'QR Scan & Pay',
        scanDesc: 'Fast and secure QR payments',
        pos: 'POS Acceptance',
        posDesc: 'Accept all payment methods',
        ecosystem: 'Complete Ecosystem',
        ecosystemDesc: 'Personal and merchant solutions',
        instant: 'Instant Transfer',
        instantDesc: 'Real-time money movement',
        multilang: 'Multi-Language',
        multilangDesc: 'AR, EN, HI, UR, BN support',
        secure: 'Secure & Modern',
        secureDesc: 'Bank-grade security'
      }
    },
    pos: {
      title: 'POS Devices',
      subtitle: 'Modern Android POS Solutions',
      description: 'Fast activation, wireless connectivity, and direct integration with ROYA wallet',
      features: {
        wireless: 'Wireless Connection',
        wirelessDesc: 'Freedom to accept payments anywhere',
        fast: 'Fast Activation',
        fastDesc: 'Get started in minutes',
        integrated: 'Direct Integration',
        integratedDesc: 'Connected to ROYA wallet',
        support: '24/7 Support',
        supportDesc: 'Always here to help'
      }
    },
    wallet: {
      title: 'ROYA Wallet',
      subtitle: 'Your Complete Financial Solution',
      description: 'Personal and merchant wallets with instant withdrawals, IBAN, top-up, and payout features',
      personal: 'Personal Wallet',
      personalDesc: 'Manage your money with ease',
      merchant: 'Merchant Wallet',
      merchantDesc: 'Business account with advanced features',
      features: {
        instant: 'Instant Withdrawal',
        iban: 'IBAN Integration',
        topup: 'Easy Top-Up',
        payout: 'Quick Payouts'
      }
    },
    about: {
      title: 'About ROYA Business',
      subtitle: 'Built by Sameer Rais Ahmad',
      description: 'A complete fintech ecosystem with more than ten years of experience in the Saudi market',
      story: [
        'Born and raised in Saudi Arabia',
        'Helping thousands of merchants across the Kingdom',
        'Deep understanding of local business needs',
        'Our vision is growth, trust, and unity',
        'We grow with our users - Let\'s grow together',
        'Proudly supporting Saudi Vision 2030 digital transformation'
      ],
      vision: 'Our Vision',
      visionText: 'Growth, Trust & Unity'
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in Touch',
      location: 'Jeddah, Saudi Arabia',
      whatsapp: 'WhatsApp',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        message: 'Message',
        submit: 'Send Message',
        success: 'Message sent successfully!'
      }
    },
    footer: {
      tagline: "Let's Grow Together",
      rights: '© 2025 ROYA Business. All rights reserved.',
      built: 'Powered from the heart of Saudi Arabia — built to support the Kingdom\'s digital transformation and fully aligned with Vision 2030.',
      ecosystem: 'A proudly Saudi-born financial ecosystem — crafted to empower merchants, elevate payment experiences, and shape the future of digital commerce in the Kingdom.'
    }
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      posDevices: 'أجهزة نقاط البيع',
      wallet: 'المحفظة',
      whoIsRoyaFor: 'لمن روية؟',
      about: 'من نحن',
      contact: 'اتصل بنا'
    },
    home: {
      heroTitle: 'مدفوعات ذكية للجميع',
      heroSubtitle: 'لننمو معاً',
      heroDescription: 'نظام رؤيا يساعد التجار والأفراد على النمو معاً',
      getStarted: 'ابدأ الآن',
      learnMore: 'اعرف المزيد',
      featuresTitle: 'خدماتنا',
      visionTitle: 'رؤيتنا',
      visionDescription: 'صُنعت في المملكة العربية السعودية، من أجل المملكة — مصممة لتسريع المدفوعات الرقمية بما يتماشى مع رؤية 2030. رؤيا ليست مجرد تطبيق تقني مالي — بل هي نظام مالي سعودي متكامل يدمج المدفوعات والمحافظ وأجهزة نقاط البيع ورموز QR والأدوات التجارية وخدمات التجار في منصة موحدة.',
      services: {
        qrPayments: 'مدفوعات QR',
        qrDesc: 'مدفوعات سريعة وآمنة برمز الاستجابة',
        posDevices: 'أجهزة نقاط البيع',
        posDesc: 'أجهزة دفع لاسلكية حديثة',
        digitalWallet: 'محفظة رقمية',
        walletDesc: 'حلول محفظة شخصية وتجارية',
        instantTransfers: 'تحويلات فورية',
        transferDesc: 'إرسال واستقبال الأموال فوراً',
        paymentGateway: 'بوابة الدفع',
        gatewayDesc: 'حلول دفع كاملة للأعمال',
        virtualCards: 'بطاقات افتراضية',
        virtualDesc: 'بطاقات رقمية للدفع عبر الإنترنت',
        physicalCards: 'بطاقات فعلية',
        physicalDesc: 'بطاقات دفع مميزة',
        loans: 'القروض',
        loansDesc: 'قريباً'
      },
      features: {
        scan: 'مسح ودفع QR',
        scanDesc: 'مدفوعات سريعة وآمنة',
        pos: 'قبول نقاط البيع',
        posDesc: 'قبول جميع طرق الدفع',
        ecosystem: 'نظام متكامل',
        ecosystemDesc: 'حلول شخصية وتجارية',
        instant: 'تحويل فوري',
        instantDesc: 'حركة أموال لحظية',
        multilang: 'متعدد اللغات',
        multilangDesc: 'دعم AR، EN، HI، UR، BN',
        secure: 'آمن وحديث',
        secureDesc: 'أمان بمستوى البنوك'
      }
    },
    pos: {
      title: 'أجهزة نقاط البيع',
      subtitle: 'حلول POS حديثة تعمل بنظام أندرويد',
      description: 'تفعيل سريع، اتصال لاسلكي، وتكامل مباشر مع محفظة رؤيا',
      features: {
        wireless: 'اتصال لاسلكي',
        wirelessDesc: 'حرية قبول المدفوعات في أي مكان',
        fast: 'تفعيل سريع',
        fastDesc: 'ابدأ في دقائق',
        integrated: 'تكامل مباشر',
        integratedDesc: 'متصل بمحفظة رؤيا',
        support: 'دعم 24/7',
        supportDesc: 'نحن دائماً هنا للمساعدة'
      }
    },
    wallet: {
      title: 'محفظة رؤيا',
      subtitle: 'حلك المالي الكامل',
      description: 'محافظ شخصية وتجارية مع سحب فوري، IBAN، شحن رصيد، ومدفوعات',
      personal: 'محفظة شخصية',
      personalDesc: 'إدارة أموالك بسهولة',
      merchant: 'محفظة تاجر',
      merchantDesc: 'حساب تجاري بمزايا متقدمة',
      features: {
        instant: 'سحب فوري',
        iban: 'تكامل IBAN',
        topup: 'شحن سهل',
        payout: 'مدفوعات سريعة'
      }
    },
    about: {
      title: 'عن رؤيا للأعمال',
      subtitle: 'صُنعت بواسطة سمير رئيس أحمد',
      description: 'نظام تقني مالي متكامل بخبرة تزيد عن عشر سنوات في السوق السعودي',
      story: [
        'وُلد ونشأ في المملكة العربية السعودية',
        'مساعدة آلاف التجار في جميع أنحاء المملكة',
        'فهم عميق لاحتياجات الأعمال المحلية',
        'رؤيتنا هي النمو والثقة والوحدة',
        'ننمو مع مستخدمينا - لننمو معاً',
        'دعم التحول الرقمي لرؤية السعودية 2030 بكل فخر'
      ],
      vision: 'رؤيتنا',
      visionText: 'النمو، الثقة والوحدة'
    },
    contact: {
      title: 'اتصل بنا',
      subtitle: 'تواصل معنا',
      location: 'جدة، المملكة العربية السعودية',
      whatsapp: 'واتساب',
      form: {
        name: 'الاسم الكامل',
        email: 'البريد الإلكتروني',
        phone: 'رقم الهاتف',
        message: 'الرسالة',
        submit: 'إرسال الرسالة',
        success: 'تم إرسال الرسالة بنجاح!'
      }
    },
    footer: {
      tagline: 'لننمو معاً',
      rights: '© 2025 رؤيا للأعمال. جميع الحقوق محفوظة.',
      built: 'صُنعت من قلب المملكة العربية السعودية — لدعم التحول الرقمي للمملكة ومتوافقة بالكامل مع رؤية 2030.',
      ecosystem: 'نظام مالي سعودي الأصل بكل فخر — صُنع لتمكين التجار، وتحسين تجارب الدفع، وتشكيل مستقبل التجارة الرقمية في المملكة.'
    }
  }
};

const languageNames = {
  en: 'English',
  ar: 'العربية',
  hi: 'हिन्दी',
  ur: 'اردو',
  bn: 'বাংলা'
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [direction, setDirection] = useState('ltr');

  useEffect(() => {
    // Set document direction based on language
    const dir = ['ar', 'ur'].includes(language) ? 'rtl' : 'ltr';
    setDirection(dir);
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (!value) return key;
    }
    
    return value;
  };

  const changeLanguage = (lang) => {
    if (languageNames[lang]) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, direction, languageNames }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
